import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Search, XCircle, Receipt, Download, Filter, ChevronDown, ChevronUp, MoreHorizontal, ArrowRightLeft } from 'lucide-react'
import {
  Button, Input, Card, Badge, Label,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui'
import {
  getTransacciones, anularPago, getComprobanteUrl,
  getCargosByJugador, getSaldoFavor, aplicarSaldoFavorACargo,
} from '../../../lib/supabase'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../ui/dropdown-menu'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { exportPagosExcel } from '../../../utils/pagosExport'
import { formatDate, formatMoney } from '../../../utils/helpers'
import RegistrarPagoModal from './RegistrarPagoModal'

const METODO_LABELS = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  saldo_favor: 'Saldo a favor',
  otro: 'Otro',
}

const METODO_COLORS = {
  efectivo: 'bg-green-100 text-green-800',
  transferencia: 'bg-blue-100 text-blue-800',
  tarjeta: 'bg-purple-100 text-purple-800',
  saldo_favor: 'bg-emerald-100 text-emerald-800',
  otro: 'bg-gray-100 text-gray-800',
}

// Desglose de a que se aplico el pago. Vive fuera del render de la tabla porque
// lo usan las dos vistas: la columna "Aplicado a" en escritorio y el detalle
// expandible en movil.
const AplicadoA = ({ tx }) => {
  const detalles = tx.pagos_transacciones_detalle || []
  const sf = (tx.pagos_saldo_favor || []).find((s) => s.activo !== false)
  const disponibleSF = Number(sf?.saldo_disponible ?? 0)
  const usadoSF = Number(sf?.monto_usado ?? 0)

  const lineasCargos = detalles.map((d, i) => (
    <div key={d.id || i} className="flex items-baseline justify-between gap-2">
      <span className="text-foreground">{d.pagos_cargos?.pagos_conceptos?.nombre || 'cargo'}</span>
      <span className="font-medium text-foreground tabular-nums">{formatMoney(d.monto_aplicado)}</span>
    </div>
  ))

  if (detalles.length > 0 && !sf) {
    return <div className="space-y-0.5">{lineasCargos}</div>
  }

  if (detalles.length > 0 && sf) {
    return (
      <div className="space-y-1">
        {lineasCargos}
        <div className="pt-1 border-t border-dashed border-emerald-200 flex items-baseline justify-between gap-2">
          <span className="text-emerald-700">Saldo a favor</span>
          <span className="font-medium text-emerald-700 tabular-nums">{formatMoney(disponibleSF + usadoSF)}</span>
        </div>
        {disponibleSF > 0 && (
          <p className="text-[10px] text-emerald-600">Disponible: <strong>{formatMoney(disponibleSF)}</strong></p>
        )}
      </div>
    )
  }

  if (sf) {
    const totalmenteUsado = disponibleSF <= 0 && usadoSF > 0
    return (
      <div className="space-y-0.5">
        <Badge variant="outline" className={`text-[10px] ${totalmenteUsado ? 'text-muted-foreground border-border' : 'text-emerald-600 border-emerald-300'}`}>
          Saldo a favor
        </Badge>
        <p className={`text-[10px] ${totalmenteUsado ? 'text-muted-foreground' : 'text-emerald-700'}`}>
          {totalmenteUsado
            ? `Aplicado completo (${formatMoney(Number(sf.monto || 0))})`
            : <>Disponible: <strong>{formatMoney(disponibleSF)}</strong> de {formatMoney(Number(sf.monto || 0))}</>}
        </p>
      </div>
    )
  }

  return <span className="text-muted-foreground">—</span>
}

const PagosTab = ({ temporadaId, temporadaSelector = null }) => {
  const { prompt, alert, ConfirmDialog } = useConfirmDialog()
  const guard = useUnsavedGuard({ onClose: () => setAplicarDialog({ open: false, tx: null }) })
  const [transacciones, setTransacciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [metodoFilter, setMetodoFilter] = useState('todos')
  const [estatusFilter, setEstatusFilter] = useState('activos')
  const [registrarOpen, setRegistrarOpen] = useState(false)
  const [aplicarDialog, setAplicarDialog] = useState({ open: false, tx: null })
  const [cargosPendientes, setCargosPendientes] = useState([])
  const [saldosDisponibles, setSaldosDisponibles] = useState([])
  const [selectedCargoId, setSelectedCargoId] = useState('')
  const [montoAplicar, setMontoAplicar] = useState('')
  const [aplicando, setAplicando] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await getTransacciones()
    if (error) console.error('[PagosTab] load error:', error)
    setTransacciones(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleAplicarSaldo = async (tx) => {
    // Cargar cargos pendientes del jugador y saldos disponibles
    const [cargosRes, saldosRes] = await Promise.all([
      getCargosByJugador(tx.jugador_id),
      getSaldoFavor(tx.jugador_id),
    ])
    const pendientes = (cargosRes.data || []).filter((c) => c.estatus !== 'pagado' && c.estatus !== 'cancelado' && Number(c.saldo) > 0)
    setCargosPendientes(pendientes)
    setSaldosDisponibles(saldosRes.data || [])
    setSelectedCargoId('')
    setMontoAplicar('')
    guard.resetDirty()
    setAplicarDialog({ open: true, tx })
  }

  const handleConfirmAplicar = async () => {
    if (!selectedCargoId || !montoAplicar || !saldosDisponibles.length) return
    setAplicando(true)

    let restante = Number(montoAplicar)
    // Consumir saldos en orden FIFO (más antiguo primero)
    for (const saldo of saldosDisponibles) {
      if (restante <= 0) break
      const disponible = Number(saldo.saldo_disponible || 0)
      if (disponible <= 0) continue
      const aplicar = Math.min(restante, disponible)
      const { error } = await aplicarSaldoFavorACargo(saldo.id, selectedCargoId, aplicar)
      if (error) { setAplicando(false); await alert('Error', error.message || 'No se pudo aplicar el saldo'); return }
      restante -= aplicar
    }

    setAplicando(false)
    guard.resetDirty()
    setAplicarDialog({ open: false, tx: null })
    load()
  }

  const handleAnular = async (tx) => {
    const motivo = await prompt('Anular pago', 'Motivo de la anulacion:', { placeholder: 'Motivo...' })
    if (motivo === null) return
    const { error } = await anularPago(tx.id, motivo)
    if (error) { await alert('Error', error.message || 'No se pudo anular el pago'); return }
    load()
  }

  // Esta pantalla es el LIBRO DE INGRESOS: solo cash-in real del jugador.
  // Las aplicaciones internas de saldo a favor -> cargo NO son ingresos,
  // son reclasificaciones. Viven en el Estado de Cuenta del jugador, no aqui.
  const filtered = transacciones.filter((tx) => {
    if (tx.metodo_pago === 'saldo_favor') return false
    const matchSearch = !searchTerm ||
      tx.jugadores?.nombre_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.referencia?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchMetodo = metodoFilter === 'todos' || tx.metodo_pago === metodoFilter
    const matchEstatus = estatusFilter === 'todos' ||
      (estatusFilter === 'activos' && !tx.anulado) ||
      (estatusFilter === 'anulados' && tx.anulado)
    return matchSearch && matchMetodo && matchEstatus
  })

  return (
    <div className="space-y-4">
      {/* El selector va en su propia fila: compartiendola no tenia limite de ancho,
          aplastaba la busqueda y la lupa terminaba encima del boton de filtros. */}
      {temporadaSelector && <div className="min-w-0">{temporadaSelector}</div>}

      {/* Una sola toolbar: los botones sueltos arriba quedaban como dos iconos
          flotando sin contexto una vez que colapsan su etiqueta en movil. */}
      <div className="flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por jugador o referencia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden lg:block whitespace-nowrap">
          <span className="font-medium text-foreground">{filtered.length}</span> pagos
        </span>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3">
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
        </Button>
        {filtered.length > 0 && (
          <Button
            variant="outline"
            onClick={() => exportPagosExcel(filtered)}
            className="h-10 w-10 p-0 shrink-0"
            aria-label="Exportar a Excel"
            title="Exportar a Excel"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
        <Button onClick={() => setRegistrarOpen(true)} className="h-10 shrink-0" aria-label="Registrar pago" title="Registrar pago">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Registrar pago</span>
        </Button>
      </div>

      {showFilters && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Metodo de pago</Label>
              <Select value={metodoFilter} onValueChange={setMetodoFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Estatus</Label>
              <Select value={estatusFilter} onValueChange={setEstatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activos">Activos</SelectItem>
                  <SelectItem value="anulados">Anulados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(metodoFilter !== 'todos' || estatusFilter !== 'todos') && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => { setMetodoFilter('todos'); setEstatusFilter('todos') }}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : filtered.length === 0 ? (
        <Card className="py-8 text-center text-muted-foreground">
          <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>No hay pagos registrados</p>
        </Card>
      ) : (
        <Card>
          {/* Movil: lista tipo estado de cuenta. Una tabla de 7 columnas no cabe en
              telefono, y el scroll horizontal escondia el metodo y las acciones.
              El monto va alineado a la derecha y tabular para poder comparar cifras
              recorriendo una sola columna. */}
          <div className="sm:hidden divide-y px-4">
            {filtered.map((tx) => {
              const sf = (tx.pagos_saldo_favor || []).find((s) => s.activo !== false)
              const disponibleSF = Number(sf?.saldo_disponible ?? 0)
              const sinAplicar = (tx.pagos_transacciones_detalle || []).length === 0
              const meta = [
                formatDate(tx.fecha_pago),
                METODO_LABELS[tx.metodo_pago] || tx.metodo_pago,
                tx.referencia
              ].filter(Boolean).join(' · ')

              const abierto = expandedId === tx.id

              return (
                <div key={tx.id} className={`py-3 ${tx.anulado ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    {/* Toda la fila abre el detalle: en movil el area tactil debe ser grande */}
                    <button
                      type="button"
                      onClick={() => setExpandedId(abierto ? null : tx.id)}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                      aria-expanded={abierto}
                    >
                      <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${abierto ? 'rotate-180' : ''}`} />
                      <span className="min-w-0">
                        <span className={`block font-medium text-sm truncate ${tx.anulado ? 'line-through' : ''}`}>
                          {tx.jugadores?.nombre_completo || '—'}
                        </span>
                        <span className="block text-xs text-muted-foreground truncate">{meta}</span>
                        {tx.anulado ? (
                          <Badge variant="destructive" className="text-[10px] mt-1">Anulado</Badge>
                        ) : disponibleSF > 0 && (
                          <span className="block text-[11px] text-emerald-700 mt-0.5">
                            Saldo a favor disponible: <strong>{formatMoney(disponibleSF)}</strong>
                          </span>
                        )}
                      </span>
                    </button>
                    <div className="flex items-start gap-1 shrink-0">
                      <span className={`text-sm font-semibold tabular-nums ${tx.anulado ? 'line-through' : ''}`}>
                        {formatMoney(tx.monto)}
                      </span>
                      {!tx.anulado && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 -mt-0.5" title="Más acciones">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {sinAplicar && (
                              <DropdownMenuItem onClick={() => handleAplicarSaldo(tx)}>
                                <ArrowRightLeft className="h-4 w-4" />
                                Aplicar a un cargo
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleAnular(tx)}
                              className="text-destructive focus:text-destructive"
                            >
                              <XCircle className="h-4 w-4" />
                              Anular pago
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>

                  {/* Detalle: exactamente las columnas que la tabla muestra en escritorio */}
                  {abierto && (
                    <dl className="mt-3 ml-6 space-y-2 text-xs border-l pl-3">
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Método</dt>
                        <dd>
                          <Badge className={`${METODO_COLORS[tx.metodo_pago] || ''} text-[10px]`}>
                            {METODO_LABELS[tx.metodo_pago] || tx.metodo_pago}
                          </Badge>
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Referencia</dt>
                        <dd className="text-right">{tx.referencia || '—'}</dd>
                      </div>
                      {tx.registrado_por_staff?.nombre_completo && (
                        <div className="flex items-baseline justify-between gap-2">
                          <dt className="text-muted-foreground">Registró</dt>
                          <dd className="text-right">{tx.registrado_por_staff.nombre_completo}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-muted-foreground mb-1">Aplicado a</dt>
                        <dd><AplicadoA tx={tx} /></dd>
                      </div>
                      {tx.anulado && (
                        <div className="pt-1 border-t border-dashed">
                          <dt className="text-muted-foreground mb-1">Anulación</dt>
                          <dd className="text-muted-foreground leading-tight">
                            {tx.anulado_por_staff?.nombre_completo && <p>{tx.anulado_por_staff.nombre_completo}</p>}
                            {tx.anulado_at && <p>{formatDate(tx.anulado_at)}</p>}
                            {tx.anulado_motivo && <p className="italic">{tx.anulado_motivo}</p>}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              )
            })}
          </div>

          {/* Escritorio: la tabla completa, con todas las columnas */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Jugador</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Metodo</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Aplicado a</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((tx) => (
                  <TableRow key={tx.id} className={tx.anulado ? 'opacity-50 line-through' : ''}>
                    <TableCell className="text-sm whitespace-nowrap">{formatDate(tx.fecha_pago)}</TableCell>
                    <TableCell className="font-medium text-sm">{tx.jugadores?.nombre_completo || '—'}</TableCell>
                    <TableCell className="text-sm font-medium">{formatMoney(tx.monto)}</TableCell>
                    <TableCell>
                      <Badge className={`${METODO_COLORS[tx.metodo_pago] || ''} text-xs`}>
                        {METODO_LABELS[tx.metodo_pago] || tx.metodo_pago}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {tx.referencia || '—'}
                      {tx.registrado_por_staff?.nombre_completo && (
                        <p className="text-[10px] text-muted-foreground">{tx.registrado_por_staff.nombre_completo}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <AplicadoA tx={tx} />
                    </TableCell>
                    <TableCell>
                      {tx.anulado ? (
                        <div className="space-y-0.5">
                          <Badge variant="destructive" className="text-[10px]">Anulado</Badge>
                          <div className="text-[10px] text-muted-foreground leading-tight">
                            {tx.anulado_por_staff?.nombre_completo && <p>{tx.anulado_por_staff.nombre_completo}</p>}
                            {tx.anulado_at && <p>{formatDate(tx.anulado_at)}</p>}
                            {tx.anulado_motivo && <p className="italic truncate max-w-[120px]" title={tx.anulado_motivo}>{tx.anulado_motivo}</p>}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          {(tx.pagos_transacciones_detalle || []).length === 0 && (
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleAplicarSaldo(tx)} title="Aplicar a cargo">
                              <ArrowRightLeft className="h-3 w-3 mr-1" /> Aplicar
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleAnular(tx)} title="Anular pago">
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <RegistrarPagoModal
        open={registrarOpen}
        onOpenChange={setRegistrarOpen}
        temporadaId={temporadaId}
        onSuccess={() => { setRegistrarOpen(false); load() }}
      />
      {/* Dialog aplicar saldo a favor */}
      <Dialog open={aplicarDialog.open} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aplicar saldo a favor</DialogTitle>
          </DialogHeader>
          {aplicarDialog.tx && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-800">{aplicarDialog.tx.jugadores?.nombre_completo}</p>
                  <p className="text-xs text-emerald-600">Saldo a favor disponible</p>
                </div>
                <span className="text-lg font-bold text-emerald-700">
                  {formatMoney(saldosDisponibles.reduce((sum, s) => sum + Number(s.saldo_disponible || 0), 0))}
                </span>
              </div>

              {saldosDisponibles.reduce((sum, s) => sum + Number(s.saldo_disponible || 0), 0) <= 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Este saldo a favor ya fue utilizado completamente.</p>
              ) : cargosPendientes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hay cargos pendientes.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Aplicar a</Label>
                    <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
                      {cargosPendientes.map((c) => {
                        const isSelected = selectedCargoId === c.id
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              guard.markDirty()
                              setSelectedCargoId(c.id)
                              const totalDisponible = saldosDisponibles.reduce((sum, s) => sum + Number(s.saldo_disponible || 0), 0)
                              setMontoAplicar(Math.min(Number(c.saldo), totalDisponible).toFixed(2))
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${isSelected ? 'bg-primary/10' : 'hover:bg-muted'}`}
                          >
                            <div className="min-w-0">
                              <p className="font-medium truncate">{c.pagos_conceptos?.nombre || 'Cargo'}</p>
                              {c.pagos_planes?.nombre && <p className="text-[10px] text-muted-foreground truncate">{c.pagos_planes.nombre}</p>}
                            </div>
                            <Badge variant={isSelected ? 'default' : 'secondary'} className="text-xs shrink-0 ml-2">
                              {formatMoney(c.saldo)}
                            </Badge>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {selectedCargoId && (
                    <div className="space-y-2">
                      <Label>Monto a aplicar</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={montoAplicar}
                        onChange={(e) => setMontoAplicar(e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
            <Button onClick={handleConfirmAplicar} disabled={!selectedCargoId || !montoAplicar || aplicando}>
              {aplicando ? 'Aplicando...' : `Aplicar ${formatMoney(montoAplicar || 0)}`}
            </Button>
          </DialogFooter>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  )
}

export default PagosTab
